import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import useExecutiveDashboardData from '../../../hooks/useExecutiveDashboardData';
import useExecutiveDashboardCalculations, {
    incomeList,
    netMunicipalsList,
    opExList,
    fuelExpenseList,
    nonOpExList,
    netInterestExpenseList,
    revalTaxList,
    badDebtsList,
    lettingCostList,
} from '../../../hooks/useExecutiveDashboardCalculations';
import MonthlyArrearsTable from './MonthlyArrearsTable';
import 'react-loading-skeleton/dist/skeleton.css';
import OpsIncidentGraph from './OpsIncidentGraph';
import CSIProgrammesData from './CSIProgrammesData';
import MediaAndPromotionsData from './MediaAndPromotionsData';
import OpsComplianceStats from './OpsComplianceStats';
import OpsSustainabilityStats from './OpsSustainabilityData';
import ArrearsLegalData from './ArrearsLegalData';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import TenancyStatsTable from './TenancyStatsTable';
import AnchorTradingData from './AnchorTradingData';
import CondensedIncomeStatementTable from './CondensedIncomeStatementTable';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import '@/styles/ExecutiveDashStyles.scss';
import UserAvatarSVG from '@/components/UserAvatarSVG'
import * as Sentry from '@sentry/react';
import 'react-tooltip/dist/react-tooltip.css';

const AdminDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { propertyData,
        totalArea,
        condensedIncomeData,
        condensedIncomeDataPrev,
        rrerData,
        totalOccupiedGLA,
        portfolioEscalation,
        portfolioEscalationProperties,
        period,
        loading,
        errors,
        hasErrors,
        refetch,
        monthName, year, prevYear } = useExecutiveDashboardData();

    const {
        averageRateOccupiedGLA,
        averageRateOccupiedGLAYtd,
        averageRateOccupiedGLAPrev,
        averageRateGLA,
        averageRateGLAYtd,
        averageRateGLAPrev,
        recoveryRates,
        recoveryRatioUtilities,
        expenseRatioCurrent,
        expenseRatioYTD,
        expenseRatioPrev,
        propertyExpenseRatioMonth,
        propertyExpenseRatioYTD,
        propertyExpenseRatioPrev,
        badDebts: badDebtPercentages,
        billings,
    } = useExecutiveDashboardCalculations(condensedIncomeData, condensedIncomeDataPrev, period, totalArea, totalOccupiedGLA, rrerData);

    const [sessionExpired] = useState(false);

    useEffect(() => { document.title = "CapTrac | Executive Dashboard"; }, []);

    // ----- Tooltip HTML Generators -----

    const _getGLATooltipHtml = () => {
        if (!propertyData) return '';
        // Table header
        let html = `<table>
        <thead>
            <tr>
                <th>Property</th>
                <th>GLA (m²)</th>
            </tr>
        </thead>
        <tbody>`;
        // Table rows
        html += Object.entries(propertyData)
            .filter(([name]) => name !== "Unity Centre")
            .map(([name, area]) => {
                const formatted = Number(area)
                    .toLocaleString('fr-FR', { maximumFractionDigits: 0 })
                    .replace(/\u202F/g, '  ');
                return `<tr>
                <td>${name}</td>
                <td>${formatted}</td>
            </tr>`;
            })
            .join('');
        html += '</tbody></table>';
        return html;
    };

    const _getIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += incomeList.map(caption => {
            // Sum all items for this caption
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            // Convert to millions and format
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const _getNetMunicipalsTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += netMunicipalsList.map(caption => {
            // Sum all items for this caption
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            // Convert to millions and format
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const _getVarianceTooltipHtml = (actual, budget, labelActual = "Actual YTD", labelBudget = "Budget YTD") => {
        return `
        <table>
            <thead>
                <tr>
                    <th>Value</th>
                    <th>Amount (R'm)</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>${labelActual}</td>
                    <td>${actual}</td>
                </tr>
                <tr>
                    <td>${labelBudget}</td>
                    <td>${budget}</td>
                </tr>
            </tbody>
        </table>
    `;
    };

    const _getOperatingExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';

        // Helper to sum and format a list
        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return {
                sum,
                formatted: inMillions.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/\u202F/g, '  ')
            };
        };

        const opEx = sumAndFormat(opExList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);

        const total = opEx.sum + badDebts.sum + lettingCost.sum;
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/\u202F/g, '  ');

        let html = `<table>
        <thead>
            <tr>
                <th>Component</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>
            <tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>
            <tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>
            <tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>
            <tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>
        </tbody>
    </table>`;
        return html;
    };

    const _getFuelTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += fuelExpenseList.map(caption => {
            // Sum all items for this caption
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            // Convert to millions and format
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const _getNetOperatingIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        // Helper to sum and format a category
        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _getNonOperatingExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        let total = 0;
        html += nonOpExList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            total += value;
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        // Add total row
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).replace(/\u202F/g, '  ');
        html += `<tr style="font-weight:bold;">
        <td>Total</td>
        <td>${totalFormatted}</td>
    </tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _getNetOperatingIncomeBeforeFinanceTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);
        const nonOpEx = sumAndFormat(nonOpExList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;
        html += `<tr><td>Non operating expenses</td><td>${nonOpEx.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum + nonOpEx.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _getNetInterestExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        let total = 0;
        html += netInterestExpenseList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            total += value;
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        // Add total row
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).replace(/\u202F/g, '  ');
        html += `<tr style="font-weight:bold;">
        <td>Total</td>
        <td>${totalFormatted}</td>
    </tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _getRevalAndTaxTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += revalTaxList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const _getNetIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        // Helper to sum and format a category
        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);
        const nonOpEx = sumAndFormat(nonOpExList);
        const interest = sumAndFormat(netInterestExpenseList);
        const revalTax = sumAndFormat(revalTaxList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;
        html += `<tr><td>Non operating expenses</td><td>${nonOpEx.formatted}</td></tr>`;
        html += `<tr><td>Net interest expense</td><td>${interest.formatted}</td></tr>`;
        html += `<tr><td>Revaluation and tax</td><td>${revalTax.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum + nonOpEx.sum + interest.sum + revalTax.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const getPortfolioEscalationTooltipHtml = () => {
        if (!portfolioEscalationProperties || portfolioEscalationProperties.length === 0) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Property</th>
                <th>Escalation (%)</th>
            </tr>
        </thead>
        <tbody>`;
        html += portfolioEscalationProperties.map(p => {
            const formatted = p.escalationPercentage.toLocaleString('en-ZA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            return `<tr>
                <td>${p.propertyName}</td>
                <td>${formatted}%</td>
            </tr>`;
        }).join('');
        // Total row
        html += `<tr style="font-weight:bold;">
            <td>Portfolio Total</td>
            <td>${portfolioEscalation !== null ? portfolioEscalation.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}%</td>
        </tr>`;
        html += '</tbody></table>';
        return html;
    };

    return (
        <div>
            {sessionExpired ? (
                <div className="error-message">
                    <p>Your session has expired. Please log in again.</p>
                    <button onClick={() => navigate('/')}>Go to Login</button>
                </div>
            ) : loading ? (
                <HexagonLoader />
            ) : (
                <>
                    <div>
                        {(
                            <h1 className="dashboard-greeting">
                                <UserAvatarSVG />
                                Hello {user.FirstName || user.firstName}
                            </h1>
                        )}
                        <h3>Executive Dashboard</h3>
                        <div className="financial-data-section">
                            {hasErrors && (
                                <div className="error-message">
                                    {errors.map((err, i) => <p key={i}>{err}</p>)}
                                    <button onClick={refetch}>Try Again</button>
                                </div>
                            )}
                            {/*Condensed Income Statement table*/}
                            {period && propertyData && (
                                <CondensedIncomeStatementTable
                                    condensedIncomeData={condensedIncomeData}
                                    period={period}
                                    totalArea={totalArea}
                                    propertyData={propertyData}
                                />
                            )}
                            <div className="dashboard-row">
                                <div className="dashboard-table-wrapper">
                                    {/*Rental, Recovery and Expense Ratios Table*/}
                                    {rrerData && (
                                        <div className="rre-ratios-container">
                                            <table className="rre-ratios-table">
                                                <thead style={{ height: '5vh', textAlign: 'left' }}>
                                                    <tr>
                                                        <th>Rental, Recovery and Expense Ratios</th>
                                                        <th>
                                                            Month<br />
                                                            {monthName} {year}
                                                        </th>
                                                        <th>
                                                            Year to Date<br />
                                                            {monthName} {year}
                                                        </th>
                                                        <th>
                                                            Year Ended<br />
                                                            December {prevYear}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Portfolio escalation (%)</td>
                                                        <td data-tooltip-id="tt" data-tooltip-content={getPortfolioEscalationTooltipHtml()}>{portfolioEscalation !== null ? `${portfolioEscalation}%` : "-"}</td>
                                                        <td data-tooltip-id="tt" data-tooltip-content={getPortfolioEscalationTooltipHtml()}>{portfolioEscalation !== null ? `${portfolioEscalation}%` : "-"}</td>
                                                        <td data-tooltip-id="tt" data-tooltip-content={getPortfolioEscalationTooltipHtml()}>{portfolioEscalation !== null ? `${portfolioEscalation}%` : "-"}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Rate per m² (of occupied area)</td>
                                                        <td>{averageRateOccupiedGLA}</td>
                                                        <td>{averageRateOccupiedGLAYtd}</td>
                                                        <td>{averageRateOccupiedGLAPrev}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Rate per m² (of GLA)</td>
                                                        <td>{averageRateGLA}</td>
                                                        <td>{averageRateGLAYtd}</td>
                                                        <td>{averageRateGLAPrev}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Recovery ratio - rates</td>
                                                        <td>{recoveryRates.month}</td>
                                                        <td>{recoveryRates.ytd}</td>
                                                        <td>{recoveryRates.fullYear}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Recovery ratio - utilities excl</td>
                                                        <td>{recoveryRatioUtilities.month}</td>
                                                        <td>{recoveryRatioUtilities.ytd}</td>
                                                        <td>{recoveryRatioUtilities.fullYear}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Property expense ratio</td>
                                                        <td>{propertyExpenseRatioMonth}</td>
                                                        <td>{propertyExpenseRatioYTD}</td>
                                                        <td>{propertyExpenseRatioPrev}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Expense ratio</td>
                                                        <td>{expenseRatioCurrent}</td>
                                                        <td>{expenseRatioYTD}</td>
                                                        <td>{expenseRatioPrev}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                                <div className="dashboard-table-wrapper">
                                    <TenancyStatsTable period={period} totalArea={totalArea} />
                                </div>
                            </div>
                            <div className="arrears-box">
                                <div className="left-arrears">
                                    <div className="table-wrapper">
                                        <AnchorTradingData period={period} />
                                    </div>
                                    <div className="table-wrapper">
                                        <ArrearsLegalData period={period} badDebtPercentages={badDebtPercentages} billings={billings} />
                                    </div>
                                    <div className="table-wrapper">
                                        <OpsComplianceStats period={period} />
                                    </div>
                                    <div className="table-wrapper">
                                        <OpsSustainabilityStats period={period} />
                                    </div>
                                    <div className="table-wrapper">
                                        <MediaAndPromotionsData
                                            period={period}
                                            condensedIncomeData={condensedIncomeData}
                                            condensedIncomeDataPrev={condensedIncomeDataPrev}
                                        />
                                    </div>
                                </div>
                                <div className="right-arrears">
                                    <div className="table-wrapper">
                                        <MonthlyArrearsTable period={period} />
                                    </div>
                                    <div className="table-wrapper">
                                        <OpsIncidentGraph />
                                    </div>
                                    <div className="table-wrapper">
                                        <CSIProgrammesData period={period} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <ReactTooltip
                id="tt"
                place="bottom"
                variant="dark"
                clickable={true}
                delayShow={0}
                delayHide={0}
                float={false}
                positionStrategy="fixed"
                openEvents={{ mouseenter: true }}
                closeEvents={{ mouseleave: true }}
                globalCloseEvents={{ escape: true, scroll: true, resize: true, clickOutsideAnchor: true }}
                disableTooltip={(anchor) => !anchor?.getAttribute('data-tooltip-content')}
                render={({ content }) =>
                    content ? <div dangerouslySetInnerHTML={{ __html: content }} /> : null
                }
            />
        </div>
    );
}
export default AdminDashboard;